import React, { useState, useEffect } from 'react';
import { Edit3, User, MailPlus } from 'lucide-react';
import { apiFetch } from '../../utils/api';
import { defaultDestinationForm, mapProviderToForm, buildExpiryDate, formatDate, RECORD_TYPE_OPTIONS } from './providerUtils';
import DestinationProviderFields from './DestinationProviderFields';

/** Purpose: Renders expanded provider workspace actions. Inputs: props object. Output: React render output (JSX). */
function ProviderWorkspace({
  provider,
  providerRecords,
  providerOptions,
  allPermissions,
  linkedPermissions,
  onAddProvider,
  onUpdateProvider,
  onGrantPermission,
  onOpenGrantModal
}) {
  const [editForm, setEditForm] = useState(() => mapProviderToForm(provider));
  const [isEditingProvider, setIsEditingProvider] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [editSuccess, setEditSuccess] = useState('');

  const [requestMode, setRequestMode] = useState('existing');
  const [requestDestinationProviderId, setRequestDestinationProviderId] = useState('');
  const [requestDestinationDraft, setRequestDestinationDraft] = useState(defaultDestinationForm);
  const [requestRecordType, setRequestRecordType] = useState('COMPLETE');
  const [requestNotes, setRequestNotes] = useState('');
  const [requestSendCopyToUser, setRequestSendCopyToUser] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestResult, setRequestResult] = useState('');

  useEffect(() => {
    setEditForm(mapProviderToForm(provider));
    setIsEditingProvider(false);
    setEditError('');
    setEditSuccess('');
    setRequestError('');
    setRequestResult('');
  }, [provider]);

  /** Purpose: Saves provider edits. Inputs: event. Output: Promise with operation result. */
  const handleSaveProvider = async (event) => {
    event.preventDefault();
    if (!isEditingProvider) return;
    if (!editForm.name.trim() || !editForm.organization.trim()) {
      setEditError('Provider name and organization are required.');
      return;
    }
    if (!onUpdateProvider) {
      setEditError('Provider update is not available.');
      return;
    }

    setIsSavingEdit(true);
    setEditError('');
    setEditSuccess('');
    try {
      await onUpdateProvider(provider.id, editForm);
      setEditSuccess('Provider information updated.');
    } catch (error) {
      setEditError(error.message || 'Failed to update provider.');
    } finally {
      setIsSavingEdit(false);
    }
  };

  /** Purpose: Resolves destination provider from selection or new-provider form. Inputs: mode, providerId, draft. Output: Promise with destination provider. */
  const resolveDestinationProvider = async (mode, providerId, draft) => {
    if (mode === 'existing') {
      const selected = providerOptions.find((option) => option.id === providerId);
      if (!selected) {
        throw new Error('Select a destination provider.');
      }
      return selected;
    }

    if (!draft.name.trim() || !draft.organization.trim()) {
      throw new Error('Destination provider name and organization are required.');
    }

    const created = await onAddProvider(draft);
    return created;
  };

  /** Purpose: Ensures a source-provider permission exists for provider-request authorization flow. Inputs: none. Output: Promise with permission object. */
  const ensureSourceProviderPermission = async () => {
    const existing = allPermissions.find(
      (permission) => permission.status === 'active' && permission.provider?.id === provider.id
    );
    if (existing) return existing;

    const created = await onGrantPermission({
      providerId: provider.id,
      grantedTo: provider.name,
      organization: provider.organization,
      purpose: `Authorize ${provider.organization} to release records as requested`,
      expiryDate: buildExpiryDate(90),
      accessLevel: 'READ',
      allRecords: true,
      recordIds: []
    });
    return created;
  };

  /** Purpose: Handles provider-to-provider request creation for new records. Inputs: event. Output: Promise with operation result. */
  const handleRequestNewRecords = async (event) => {
    event.preventDefault();
    setIsSubmittingRequest(true);
    setRequestError('');
    setRequestResult('');

    try {
      if (!requestRecordType) {
        throw new Error('Select a record type.');
      }

      const destination = await resolveDestinationProvider(requestMode, requestDestinationProviderId, requestDestinationDraft);
      const sourcePermission = await ensureSourceProviderPermission();

      const createResponse = await apiFetch('/provider-requests', {
        method: 'POST',
        body: JSON.stringify({
          providerName: provider.name,
          providerTitle: provider.title || '',
          organization: provider.organization,
          department: '',
          address: [provider.addressLine1, provider.addressLine2].filter(Boolean).join(' '),
          city: provider.city || '',
          state: provider.state || '',
          postalCode: provider.postalCode || '',
          phone: provider.phone || '',
          fax: provider.fax || '',
          email: provider.email || '',
          providerNpi: provider.npi || '',
          recordTypes: [requestRecordType],
          specificRecords: requestNotes || '',
          urgency: 'routine',
          notes: requestNotes || '',
          deliveryMethod: 'EMAIL',
          destinationProviderName: destination.name,
          destinationOrganization: destination.organization,
          destinationDepartment: destination.title || '',
          destinationAddress: [destination.addressLine1, destination.addressLine2].filter(Boolean).join(' '),
          destinationCity: destination.city || '',
          destinationState: destination.state || '',
          destinationPostalCode: destination.postalCode || '',
          destinationPhone: destination.phone || '',
          destinationFax: destination.fax || '',
          destinationEmail: destination.email || '',
          sendCopyToUser: requestSendCopyToUser
        })
      });

      const requestPayload = await createResponse.json().catch(() => ({}));
      if (!createResponse.ok) {
        throw new Error(requestPayload.error || 'Failed to create provider request.');
      }

      let attachMessage = 'Authorization attachment pending.';
      if (sourcePermission?.id) {
        const attachResponse = await apiFetch(`/provider-requests/${requestPayload.id}/attach-authorization`, {
          method: 'POST',
          body: JSON.stringify({ authorizationId: sourcePermission.id })
        });
        if (attachResponse.ok) {
          attachMessage = 'HIPAA authorization attached.';
        } else {
          const attachPayload = await attachResponse.json().catch(() => ({}));
          attachMessage = attachPayload.error || 'Authorization exists but requires signing before attach.';
        }
      }

      setRequestResult(
        `Request created for ${provider.name} to send records to ${destination.name}. ${attachMessage} Open Import > Request From Provider to complete/send.`
      );
    } catch (error) {
      setRequestError(error.message || 'Failed to create provider request.');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  /** Purpose: Opens request modal for provider-to-provider transfer request. Inputs: none. Output: none. */
  const openRequestModal = () => {
    setRequestError('');
    setRequestResult('');
    setShowRequestModal(true);
  };

  /** Purpose: Closes request modal. Inputs: none. Output: none. */
  const closeRequestModal = () => {
    setShowRequestModal(false);
    setRequestError('');
    setRequestResult('');
  };

  return (
    <div className="mt-4 border-t border-slate-200 pt-4 space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <form onSubmit={handleSaveProvider} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <Edit3 className="w-4 h-4" />
              Provider Info
            </h4>
            {isEditingProvider ? (
              <button
                type="button"
                onClick={() => {
                  setEditForm(mapProviderToForm(provider));
                  setEditError('');
                  setEditSuccess('');
                  setIsEditingProvider(false);
                }}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-xs hover:bg-slate-100"
              >
                Cancel Edit
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setEditError('');
                  setEditSuccess('');
                  setIsEditingProvider(true);
                }}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-xs hover:bg-slate-100"
              >
                Edit Provider
              </button>
            )}
          </div>

          {editError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {editError}
            </div>
          )}
          {editSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded-lg text-sm">
              {editSuccess}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              type="text"
              value={editForm.name}
              onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="Provider name *"
            />
            <input
              type="text"
              value={editForm.title}
              onChange={(event) => setEditForm((prev) => ({ ...prev, title: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="Title"
            />
            <input
              type="text"
              value={editForm.organization}
              onChange={(event) => setEditForm((prev) => ({ ...prev, organization: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg md:col-span-2 ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="Organization *"
            />
            <input
              type="email"
              value={editForm.email}
              onChange={(event) => setEditForm((prev) => ({ ...prev, email: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="Email"
            />
            <input
              type="text"
              value={editForm.phone}
              onChange={(event) => setEditForm((prev) => ({ ...prev, phone: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="Phone"
            />
            <input
              type="text"
              value={editForm.addressLine1}
              onChange={(event) => setEditForm((prev) => ({ ...prev, addressLine1: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg md:col-span-2 ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="Address line 1"
            />
            <input
              type="text"
              value={editForm.city}
              onChange={(event) => setEditForm((prev) => ({ ...prev, city: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="City"
            />
            <input
              type="text"
              value={editForm.state}
              onChange={(event) => setEditForm((prev) => ({ ...prev, state: event.target.value }))}
              readOnly={!isEditingProvider}
              className={`px-3 py-2 border border-slate-300 rounded-lg ${isEditingProvider ? 'bg-white' : 'bg-slate-100 text-slate-600'}`}
              placeholder="State"
            />
          </div>
          {isEditingProvider && (
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSavingEdit}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {isSavingEdit ? 'Saving...' : 'Save Provider'}
              </button>
            </div>
          )}
        </form>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-4 h-4" />
            Records Originating From This Provider ({providerRecords.length})
          </h4>
          {providerRecords.length === 0 ? (
            <p className="text-sm text-slate-500">No matched records found for this provider yet.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg bg-white">
              {providerRecords.map((record) => (
                <div key={record.id} className="px-3 py-2 border-b border-slate-100 last:border-0">
                  <p className="text-sm font-medium text-slate-900">{record.title}</p>
                  <p className="text-xs text-slate-500">{record.type} • {formatDate(record.date)}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => onOpenGrantModal(provider.id)}
              className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100"
            >
              Grant HIPAA Permission
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={openRequestModal}
          className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-100 inline-flex items-center gap-2"
        >
          <MailPlus className="w-4 h-4" />
          Request Records Transfer
        </button>
      </div>

      {linkedPermissions.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">This Provider Permission History</p>
          <div className="space-y-2">
            {linkedPermissions.slice(0, 5).map((permission) => (
              <p key={permission.id} className="text-sm text-slate-700">
                {permission.purpose} • {permission.status} • expires {formatDate(permission.expiryDate)}
              </p>
            ))}
          </div>
        </div>
      )}

      {showRequestModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <form onSubmit={handleRequestNewRecords} className="bg-white rounded-xl shadow-xl w-full max-w-5xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h4 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <MailPlus className="w-4 h-4" />
                  Request This Provider Send New Records To Another Provider
                </h4>
                <p className="text-sm text-slate-500 mt-1">
                  Creates a provider request from this source provider to a destination provider, with HIPAA setup/attach attempts included.
                </p>
              </div>
              <button
                type="button"
                onClick={closeRequestModal}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <DestinationProviderFields
              mode={requestMode}
              setMode={setRequestMode}
              selectedProviderId={requestDestinationProviderId}
              setSelectedProviderId={setRequestDestinationProviderId}
              newDestination={requestDestinationDraft}
              setNewDestination={setRequestDestinationDraft}
              providerOptions={providerOptions}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700 block">Requested record type</label>
              <select
                value={requestRecordType}
                onChange={(event) => setRequestRecordType(event.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
              >
                {RECORD_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              value={requestNotes}
              onChange={(event) => setRequestNotes(event.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              placeholder="Optional notes/instructions to include in request"
            />

            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={requestSendCopyToUser}
                onChange={(event) => setRequestSendCopyToUser(event.target.checked)}
              />
              Also request records be copied to me
            </label>

            {requestError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {requestError}
              </div>
            )}
            {requestResult && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm">
                {requestResult}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRequestModal}
                className="px-3 py-2 border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingRequest}
                className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {isSubmittingRequest ? 'Creating Request...' : 'Create Provider Request'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default ProviderWorkspace;
